import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';

import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import type { KichBanTienDo } from '../../types';

/**
 * Tiến độ một quy trình nhiều bước. Một kịch bản đang chạy TRÔNG y hệt cuộc trò chuyện thường, nên phải vẽ ra ba điều
 * câu chữ không nói: còn mấy bước, còn thiếu gì (liệt kê TÊN, không chỉ đếm — đếm thì người ta khai nhầm cái đã có),
 * đã xong chưa.
 */
export function TienDoKichBan({ kichBan }: { kichBan: KichBanTienDo }) {
  const { t, locale } = useT();
  const { ten, buoc, tongBuoc, conThieu, xong, loi } = kichBan;
  const phanTram = tongBuoc > 0 ? Math.min(100, Math.round((buoc / tongBuoc) * 100)) : 0;
  const nhanBuoc = t('troLyAiHoiKichBan.buoc', { i: soVN(buoc, locale), n: soVN(tongBuoc, locale) });

  return (
    <section
      className={cn(
        'rounded-lg border p-3.5',
        xong ? 'border-success-500 bg-success-50' : 'border-primary-200 bg-primary-50',
      )}
      aria-label={t('troLyAiHoiKichBan.tienDo', { ten })}
    >
      <div className="mb-2 flex items-center gap-2">
        {xong ? <CheckCircleOutlined className="text-xs text-success-700" /> : null}
        <p className={cn('min-w-0 truncate text-xs font-medium', xong ? 'text-success-700' : 'text-primary-700')}>{ten}</p>
        <span className="ms-auto shrink-0 text-xs tabular-nums text-content-secondary">
          {xong ? t('troLyAiHoiKichBan.daXong') : nhanBuoc}
        </span>
      </div>

      <div
        role="progressbar"
        aria-valuenow={buoc}
        aria-valuemin={0}
        aria-valuemax={tongBuoc}
        aria-label={nhanBuoc}
        className="h-1.5 overflow-hidden rounded-full bg-subtle"
      >
        <div
          className={cn('h-full rounded-full transition-[width]', xong ? 'bg-success-600' : 'bg-primary-600')}
          style={{ width: `${phanTram}%` }}
        />
      </div>

      {/* Lý do bị từ chối, ngay tại khối tiến độ: người vừa gõ sai đang nhìn ô nhập; một câu giải thích lẫn trong đoạn
          trả lời thì họ gõ lại y hệt cái vừa sai. */}
      {loi ? (
        <p className="mt-2 flex items-start gap-1.5 rounded bg-danger-50 px-2 py-1.5 text-xs leading-relaxed text-danger-700">
          <WarningOutlined className="mt-px shrink-0 text-xs" />
          <span>{loi}</span>
        </p>
      ) : null}

      {conThieu.length > 0 ? (
        <p className="mt-2 text-xs leading-relaxed text-content-secondary">
          {t('troLyAiHoiKichBan.conThieu')} <b className="font-medium">{conThieu.join(' · ')}</b>
        </p>
      ) : null}
    </section>
  );
}
